export type ContractValidateRes = {
  is_valid: boolean;
  message: string;
};

// =============================================================================
// AuthZ access management (KANP-1475, backed by KANP-1730 PR1)
//
// Mirrors the DCM /api/v1/contracts/{id}/access response shape. Server
// returns raw FGA tuple state; we derive the UI role label client-side via
// roleFromGrants() to avoid the bake-permissions staleness anti-pattern
// (Joshua Pheneger #authz 2026-06-10 thread). React Query invalidates
// `dataContractsKeys.access(id)` on every mutation so the panel stays fresh.
// =============================================================================

export type GrantRelation =
  | 'user_owner'
  | 'user_querier'
  | 'user_pii_querier'
  | 'user_viewer'
  | 'service_owner'
  | 'service_querier'
  | 'api_key_owner'
  | 'api_key_querier';

export type Grant = {
  relation: GrantRelation;
  source: string; // 'direct' or the FGA userset string (e.g. 'organization:org-1#user_admin')
};

export type PrincipalType =
  | 'user'
  | 'constellation'
  | 'organization_userset'
  | 'service'
  | 'api_key'
  | 'unknown';

export type AccessPrincipal = {
  principal: string;
  type: PrincipalType;
  grants: Grant[];
};

export type ContractAccessResponse = {
  contract_id: string;
  is_discoverable: boolean;
  total: number;
  principals: AccessPrincipal[];
};

// KANP-1474: caller's effective gates on ONE contract. Powers the
// Data Explorer Run-button gate (`can_query`) and "PII columns hidden"
// badge (`!can_query_pii`). Distinct from `ContractAccessResponse`:
// /access lists who else has access (owner-only); this is caller-scoped
// and any audience (Reader, PII Querier, Member) can call it without
// hitting a 403.
//
// `enforce` reflects DCM's runtime mode. Consumers MUST gate their
// enforcement on this flag — in SHADOW the gates are returned for
// audit/UX preview but DCM would NOT actually block the request, so
// the UI must not disable Run + must not show the PII badge (would
// regress today's behavior where users on dev01 can query everything).
export type MyContractPermissionsResponse = {
  subject: string;
  contract_id: string;
  can_view: boolean;
  can_query: boolean;
  can_query_pii: boolean;
  enforce: boolean;
};

// UI-only role taxonomy derived from grants[]. Mirrors the design-doc
// Owner / Data Reader naming. NOT a FGA relation — see roleFromGrants().
export type ContractRole = 'owner' | 'data_reader';

export type GrantContractAccessRequest = {
  principal: string; // 'user:auth0|x' or 'constellation:uuid'
  permission?: ContractRole; // omitted for constellation grants
  pii?: boolean; // omitted for constellation grants
};

export type ChangeContractAccessRequest = {
  permission?: ContractRole;
  pii?: boolean;
};

export type AccessOperationResponse = {
  contract_id: string;
  principal: string;
  operation: string;
  pii: boolean | null;
};

export type RestrictContractRequest = {
  private: boolean;
};

export type RestrictContractResponse = {
  contract_id: string;
  private: boolean;
};

export type TaskStatusResponse = {
  task_id: string;
  status: ContractTaskStatus;
  progress: number;
  progress_message: string;
  error: string | null;
};

export enum ContractTaskStatus {
  PENDING = 'PENDING',
  STARTED = 'STARTED',
  ACCESSCONTROL = 'ACCESSCONTROL',
  SUCCESS = 'SUCCESS',
  FAILURE = 'FAILURE',
  RETRY = 'RETRY',
  REVOKED = 'REVOKED'
}

export enum ContractStatus {
  DRAFT = 'DRAFT',
  QUEUED = 'QUEUED',
  PROCESSING = 'PROCESSING',
  COMPLETED = 'COMPLETED',
  FAILED = 'FAILED',
  DELETING = 'DELETING'
}

export type Contract = {
  id: string;
  contract_id: string;
  name: string;
  owner: string;
  submitted_by?: string;
  environment: string;
  status: ContractStatus;
  source_type: string;
  destination_type: string;
  created_at: string;
  updated_at: string;
  yaml_content?: string;
  secret_name?: string | null;
  secret_type?: string | null;
  secret_error?: string | null;
};

export type LatestTaskInfo = {
  task_id: string;
  task_type: string;
  status: string;
  progress: number;
  progress_message: string | null;
  started_at: string | null;
  completed_at: string | null;
  error: string | null;
};

export type ContractStatusResponse = {
  contract_id: string;
  contract_name: string;
  contract_status: string;
  owner: string;
  environment: string;
  created_at: string | null;
  updated_at: string | null;
  latest_task: LatestTaskInfo | null;
};

export type ContractsListResponse = {
  contracts: Contract[];
  total: number;
};

export enum ValidationState {
  NONE = 'NONE',
  VALIDATING = 'VALIDATING',
  VALID = 'VALID',
  INVALID = 'INVALID',
  MODIFIED = 'MODIFIED'
}

// Parsed YAML structure types
export type DataContractInfo = {
  title: string;
  version: string;
  description?: string;
  owner?: string;
  contact?: {
    name?: string;
    url?: string;
  };
  slackChannel?: string;
  tenant?: string;
};

export type ServerConfig = {
  type: string;
  host?: string;
  port?: number;
  database?: string;
  schema?: string;
  secrets?: {
    type?: string;
    secret_name?: string;
    vault_url?: string;
    vault_path?: string;
  };
};

export type DataContractServers = {
  source?: ServerConfig;
  destination?: ServerConfig;
};

export type ModelField = {
  type: string;
  required?: boolean;
  unique?: boolean;
  primary?: boolean;
  description?: string;
  config?: Record<string, any>;
};

export type DataContractModel = {
  description?: string;
  type?: string;
  fields?: Record<string, ModelField>;
};

export type DataContractYAML = {
  dataContractSpecification?: string;
  id?: string;
  info?: DataContractInfo;
  servers?: DataContractServers;
  models?: Record<string, DataContractModel>;
  terms?: {
    usage?: string;
    limitations?: string;
  };
};
